import { cn, Text } from '@viapeople/components'
import { useState } from 'react'
import { FormLabelWithTooltip } from './FormLabelWithTooltip'
import type { ComponentProps, RatingCustomProps } from './types'

type FormsRatingProps = ComponentProps & {
	value?: string
	onChange?: (value: string) => void
	readOnly?: boolean
	hasError?: boolean
}

export const FormsRating = ({ component, value, onChange, readOnly, required, hasError }: FormsRatingProps) => {
	const {
		label,
		labelColor,
		labelSize,
		labelWeight,
		count,
		defaultValue,
		options,
		tooltipEnabled,
		tooltipText,
		showColorsBeforeSelection,
	} = component.custom as RatingCustomProps

	const [internalSelected, setInternalSelected] = useState(defaultValue || '')
	const [hasBeenInteracted, setHasBeenInteracted] = useState(false)
	const selected = value !== undefined ? value : internalSelected

	const handleSelect = (e: React.MouseEvent<HTMLElement>, index: number) => {
		e.stopPropagation()
		if (readOnly) return
		const newValue = options?.[index]?.value || String(index + 1)
		setHasBeenInteracted(true)
		if (onChange) {
			onChange(newValue)
		} else {
			setInternalSelected(newValue)
		}
	}

	const selectedIndex = options?.findIndex((opt) => opt.value === selected) ?? -1

	return (
		<div className='w-full flex flex-col text-left gap-y-1 p-2'>
			{label && (
				<FormLabelWithTooltip
					label={label}
					labelColor={labelColor}
					labelSize={labelSize}
					labelWeight={labelWeight}
					required={required}
					readOnly={readOnly}
					tooltipEnabled={tooltipEnabled}
					tooltipText={tooltipText}
				/>
			)}
			<div className={cn('flex gap-2', hasError && 'border-[2px] border-error-600 rounded-md p-1')}>
				{count &&
					Array.from({ length: count }).map((_, index) => {
						const isSelected = selectedIndex === index && selectedIndex >= 0
						const optionColor = options?.[index]?.labelColor
						const hasSelection = selectedIndex >= 0
						const isColored = isSelected ? true : showColorsBeforeSelection && !!optionColor
						const backgroundColor = isColored ? optionColor || '#2563EB' : 'white'
						const opacity = showColorsBeforeSelection && hasSelection && !isSelected ? 0.3 : 1
						const textColorClass = isColored ? 'text-neutral-0' : 'text-neutral-500'

						return (
							<button
								type='button'
								// biome-ignore lint/suspicious/noArrayIndexKey: fixed rating scale where index IS the rating value
								key={`rating-option-${index}`}
								className={cn(
									'flex w-10 h-10 items-center justify-center rounded-sm border border-neutral-200',
									isColored && 'border-0',
								)}
								style={{ backgroundColor, opacity }}
								onClick={(e) => handleSelect(e, index)}>
								<Text variant='xsmall' className={textColorClass}>
									{options?.[index]?.value || index + 1}
								</Text>
							</button>
						)
					})}
			</div>
			{selectedIndex >= 0 && options?.[selectedIndex]?.valueLabel && (
				<Text
					variant='xsmall'
					weight='semibold'
					data-testid='value-label'
					className='break-words'
					style={{ color: options[selectedIndex]?.labelColor || '#1E293B' }}>
					{options[selectedIndex]?.valueLabel}
				</Text>
			)}
			{hasError && !hasBeenInteracted && (
				<Text variant='xsmall' className='text-error-600 mt-1'>
					Please change from default value
				</Text>
			)}
		</div>
	)
}
